import { MultiSlider } from "@blueprintjs/core";
import { segmentTrackColorThresholdsChanged } from "analytics/analyticTrackEvents";
import Legend from "components/layerStyleSelector/Legend";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { getColors } from "sharedStyles/capacityColorStyles";
import {
    CapacityThresholds,
    ColorMode,
    DEFAULT_CONSTRAINT_COUNT_THRESHOLD
} from "types/busFilterTypes";
import { CapacityColorSliderConfig } from "types/componentConfigPerRegion";
import "./ColorThresholdsFilter.scss";

const CONSTRAINT_COUNT_SLIDER_CONFIG: CapacityColorSliderConfig = {
    max: 20,
    labelStepSize: 5,
    stepSize: 1
};

const ColorThresholdsFilter: React.FC = () => {
    const {
        busFiltersConfiguration: {
            busFilters,
            updateCapacityThresholds,
            updateConstraintCountThresholds,
            filtersDisabled
        }
    } = useMapViewConfiguration();
    const { colorMode, capacityThresholds, constraintCountThresholds } =
        busFilters;
    const { componentConfig } = useMapViewRoutingMetadata();
    const user = useUserDataContext();

    const isConstraintCountMode = colorMode === ColorMode.CONSTRAINT_COUNT;

    const sliderConfig = isConstraintCountMode
        ? CONSTRAINT_COUNT_SLIDER_CONFIG
        : componentConfig.capacityColorSliderConfig;

    const currentThresholds = isConstraintCountMode
        ? constraintCountThresholds ?? DEFAULT_CONSTRAINT_COUNT_THRESHOLD
        : capacityThresholds;

    const onChange = (thresholds: CapacityThresholds) => {
        if (isConstraintCountMode) {
            updateConstraintCountThresholds(thresholds);
        } else {
            updateCapacityThresholds(thresholds);
            segmentTrackColorThresholdsChanged(thresholds);
        }
    };

    const colors = getColors(user.isColorblindMode);

    // For constraint count: low = good, high = poor
    // For capacity: low = poor, high = good
    const leftTrackColor = isConstraintCountMode ? colors.good : colors.poor;
    const mediumTrackColor = colors.fair;
    const rightTrackColor = isConstraintCountMode ? colors.poor : colors.good;

    // Shows a read-only legend instead of an editable slider
    const hideSlider =
        !isConstraintCountMode && componentConfig.hideCapacityColorSlider;

    return (
        <div className="ColorThresholds-wrapper">
            {!hideSlider && (
                <div className="ColorThresholds-multi-slider-wrapper">
                    <MultiSlider
                        min={0}
                        max={sliderConfig.max}
                        stepSize={sliderConfig.stepSize}
                        labelStepSize={sliderConfig.labelStepSize}
                        className="ColorThresholds-multi-slider"
                        disabled={filtersDisabled}
                    >
                        <MultiSlider.Handle
                            value={currentThresholds.mediumThreshold}
                            onChange={(evt) => {
                                onChange({
                                    ...currentThresholds,
                                    mediumThreshold: evt
                                });
                            }}
                            trackStyleBefore={{
                                backgroundColor: leftTrackColor
                            }}
                            trackStyleAfter={{
                                backgroundColor: mediumTrackColor
                            }}
                        />
                        <MultiSlider.Handle
                            value={currentThresholds.highThreshold}
                            onChange={(evt) => {
                                onChange({
                                    ...currentThresholds,
                                    highThreshold: evt
                                });
                            }}
                            trackStyleAfter={{
                                backgroundColor: rightTrackColor
                            }}
                        />
                    </MultiSlider>
                </div>
            )}
            <div className="ColorThresholds-legend-wrapper">
                <Legend hideLegendTitle={true} />
            </div>
        </div>
    );
};

export default ColorThresholdsFilter;
